/**
 * NOTE: rollup-replace-plugin handles only simple string replacements,
 * so you cannot destructure process.env like
 * const { NODE_ENV } = process.env;
 * instead, always access properties directly
 * const NODE_ENV = process.env.NODE_ENV;
 *
 * NOTE #2: we need to type envs as string | undefined
 * because envs are replaced at build time and ts narrows down the comparison between
 * two different string literals, which results in `This condition will always return 'false' or 'true'`
 *
 * NOTE #3: if you are getting `process is not defined` errors, the env is not set
 * if you want to treat missing value as a valid case, you need to initialize it in
 * rollup.config.js env
 *
 * NOTE #4: you can make the env variable available in runtime by setting it on `window.__RUNTIME_ENV__`.
 * This allows for using the same build for multiple deploys (with different environments).
 */

export const nodeEnv: string | undefined = process.env.NODE_ENV;
export const isProd = nodeEnv === 'production';
export const isTest = nodeEnv === 'test';
export const packageVersion: string = process.env.PKG_VERSION || '';
/**
 * determines if requests should be made to local services run by docker,
 * or if should access k8s cluster
 */
const isLocalEnvValue: string | undefined =
    runtimeEnv('USE_LOCAL_SERVICES') ||
    process.env.USE_LOCAL_SERVICES ||
    process.env.VUE_APP_USE_LOCAL_SERVICES;

export const isLocal = isLocalEnvValue === 'true'; // vue apps only read env variables that start with VUE_APP

// propose to change 'isLocal' to 'useLocal' for better expression
export const useLocal = isLocal;

export const localBackendUrl = runtimeEnv('LOCAL_BACKEND_URL') || process.env.LOCAL_BACKEND_URL;

export const telemetryDevEndpoint =
    runtimeEnv('TELEMETRY_BASE_URL') || process.env.TELEMETRY_BASE_URL || '';
export const telemetryApiKey =
    runtimeEnv('TELEMETRY_API_KEY') || process.env.TELEMETRY_API_KEY || '';

export const remoteServersUrl =
    runtimeEnv('REMOTE_SERVERS_BASE_URL') || process.env.REMOTE_SERVERS_BASE_URL;

export const remoteServersUrlProd =
    runtimeEnv('REMOTE_SERVERS_BASE_URL__PROD') || process.env.REMOTE_SERVERS_BASE_URL__PROD;

export const noneAuthServersUrl =
    runtimeEnv('NONE_AUTH_SERVERS_BASE_URL') || process.env.NONE_AUTH_SERVERS_BASE_URL;

export const redirectUriForRedirectFlow =
    runtimeEnv('AUTH_REDIRECT_URI_FOR_REDIRECT_FLOW') ||
    process.env.AUTH_REDIRECT_URI_FOR_REDIRECT_FLOW;
/**
 * For local development, it is possible to use oauth protected services when provided basic auth credential.
 * It should be in format `user:password`
 */
export const oauthBasicCredentials: string | undefined =
    runtimeEnv('OAUTH_BASIC_CREDENTIALS') || process.env.OAUTH_BASIC_CREDENTIALS;
/**
 * Azure oauth authentication details. Can be obtained from from azure portal.
 * Defaults are taken from `roiPE_DEV` app.
 */
export const azureClientId: string =
    runtimeEnv('AZURE_CLIENT_ID') ||
    process.env.AZURE_CLIENT_ID ||
    warnOnMissing('AZURE_CLIENT_ID');

export const azureClientIdProd: string =
    runtimeEnv('AZURE_CLIENT_ID__PROD') ||
    process.env.AZURE_CLIENT_ID__PROD ||
    warnOnMissing('AZURE_CLIENT_ID__PROD');

export const azureTentant: string =
    runtimeEnv('AZURE_TENTANT') || process.env.AZURE_TENTANT || warnOnMissing('AZURE_TENTANT');

export const useMockAuth: string | undefined =
    runtimeEnv('USE_MOCK_AUTH') || process.env.USE_MOCK_AUTH;

/**
 * Authentication against consuming 3rd party apis (e.g. API Gateway)
 */
export const gumsUsersApiOrigin: string =
    runtimeEnv('GUMS_USERS_API_ORIGIN') ||
    process.env.GUMS_USERS_API_ORIGIN ||
    warnOnMissing('GUMS_USERS_API_ORIGIN');

export const gumsUsersApiSubscriptionKey: string =
    runtimeEnv('GUMS_USERS_API_SUBSCRIPTION_KEY') ||
    process.env.GUMS_USERS_API_SUBSCRIPTION_KEY ||
    warnOnMissing('GUMS_USERS_API_SUBSCRIPTION_KEY');

export const gumsUsersApiScope: string =
    runtimeEnv('GUMS_USERS_API_SCOPE') ||
    process.env.GUMS_USERS_API_SCOPE ||
    warnOnMissing('GUMS_USERS_API_SCOPE');

export const hrUnoApiOriginDev: string =
    runtimeEnv('HR_UNO_API_ORIGIN_DEV') ||
    process.env.HR_UNO_API_ORIGIN_DEV ||
    warnOnMissing('HR_UNO_API_ORIGIN_DEV');

export const hrUnoApiOriginQas: string =
    runtimeEnv('HR_UNO_API_ORIGIN_QAS') ||
    process.env.HR_UNO_API_ORIGIN_QAS ||
    warnOnMissing('HR_UNO_API_ORIGIN_QAS');

export const hrUnoApiOriginPrd: string =
    runtimeEnv('HR_UNO_API_ORIGIN_PRD') ||
    process.env.HR_UNO_API_ORIGIN_PRD ||
    warnOnMissing('HR_UNO_API_ORIGIN_PRD');

export const hrHccApiOriginDev: string =
    runtimeEnv('HR_HCC_API_ORIGIN_DEV') ||
    process.env.HR_HCC_API_ORIGIN_DEV ||
    warnOnMissing('HR_HCC_API_ORIGIN_DEV');

export const hrHccApiOriginQas: string =
    runtimeEnv('HR_HCC_API_ORIGIN_QAS') ||
    process.env.HR_HCC_API_ORIGIN_QAS ||
    warnOnMissing('HR_HCC_API_ORIGIN_QAS');

export const hrHccApiOriginPrd: string =
    runtimeEnv('HR_HCC_API_ORIGIN_PRD') ||
    process.env.HR_HCC_API_ORIGIN_PRD ||
    warnOnMissing('HR_HCC_API_ORIGIN_PRD');

export const hrDeiApiOriginDev: string =
    runtimeEnv('HR_DEI_API_ORIGIN_DEV') ||
    process.env.HR_DEI_API_ORIGIN_DEV ||
    warnOnMissing('HR_DEI_API_ORIGIN_DEV');

export const hrEmployeeListApiOriginDev: string =
    runtimeEnv('HR_EMPLOYEELIST_API_ORIGIN_DEV') ||
    process.env.HR_EMPLOYEELIST_API_ORIGIN_DEV ||
    warnOnMissing('HR_EMPLOYEELIST_API_ORIGIN_DEV');

export const hrUnoApiSubscriptionKeyDev: string =
    runtimeEnv('HR_UNO_API_SUBSCRIPTION_KEY_DEV') ||
    process.env.HR_UNO_API_SUBSCRIPTION_KEY_DEV ||
    warnOnMissing('HR_UNO_API_SUBSCRIPTION_KEY_DEV');

export const hrUnoApiSubscriptionKeyQas: string =
    runtimeEnv('HR_UNO_API_SUBSCRIPTION_KEY_QAS') ||
    process.env.HR_UNO_API_SUBSCRIPTION_KEY_QAS ||
    warnOnMissing('HR_UNO_API_SUBSCRIPTION_KEY_QAS');

export const hrUnoApiSubscriptionKeyPrd: string =
    runtimeEnv('HR_UNO_API_SUBSCRIPTION_KEY_PRD') ||
    process.env.HR_UNO_API_SUBSCRIPTION_KEY_PRD ||
    warnOnMissing('HR_UNO_API_SUBSCRIPTION_KEY_PRD');

export const hrHccApiSubscriptionKeyDev: string =
    runtimeEnv('HR_HCC_API_SUBSCRIPTION_KEY_DEV') ||
    process.env.HR_HCC_API_SUBSCRIPTION_KEY_DEV ||
    warnOnMissing('HR_HCC_API_SUBSCRIPTION_KEY_DEV');

export const hrHccApiSubscriptionKeyQas: string =
    runtimeEnv('HR_HCC_API_SUBSCRIPTION_KEY_QAS') ||
    process.env.HR_HCC_API_SUBSCRIPTION_KEY_QAS ||
    warnOnMissing('HR_HCC_API_SUBSCRIPTION_KEY_QAS');

export const hrHccApiSubscriptionKeyPrd: string =
    runtimeEnv('HR_HCC_API_SUBSCRIPTION_KEY_PRD') ||
    process.env.HR_HCC_API_SUBSCRIPTION_KEY_PRD ||
    warnOnMissing('HR_HCC_API_SUBSCRIPTION_KEY_PRD');

export const hrDeiApiSubscriptionKeyDev: string =
    runtimeEnv('HR_DEI_API_SUBSCRIPTION_KEY_DEV') ||
    process.env.HR_DEI_API_SUBSCRIPTION_KEY_DEV ||
    warnOnMissing('HR_DEI_API_SUBSCRIPTION_KEY_DEV');

export const hrEmployeeListApiSubscriptionKeyDev: string =
    runtimeEnv('HR_EMPLOYEELIST_API_SUBSCRIPTION_KEY_DEV') ||
    process.env.HR_EMPLOYEELIST_API_SUBSCRIPTION_KEY_DEV ||
    warnOnMissing('HR_EMPLOYEELIST_API_SUBSCRIPTION_KEY_DEV');

export const hrUnoApiScopeDev: string =
    runtimeEnv('HR_UNO_API_SCOPE_DEV') ||
    process.env.HR_UNO_API_SCOPE_DEV ||
    warnOnMissing('HR_UNO_API_SCOPE_DEV');

export const hrUnoApiScopeQas: string =
    runtimeEnv('HR_UNO_API_SCOPE_QAS') ||
    process.env.HR_UNO_API_SCOPE_QAS ||
    warnOnMissing('HR_UNO_API_SCOPE_QAS');

export const hrUnoApiScopePrd: string =
    runtimeEnv('HR_UNO_API_SCOPE_PRD') ||
    process.env.HR_UNO_API_SCOPE_PRD ||
    warnOnMissing('HR_UNO_API_SCOPE_PRD');

export const hrHccApiScopeDev: string =
    runtimeEnv('HR_HCC_API_SCOPE_DEV') ||
    process.env.HR_HCC_API_SCOPE_DEV ||
    warnOnMissing('HR_HCC_API_SCOPE_DEV');

export const hrHccApiScopeQas: string =
    runtimeEnv('HR_HCC_API_SCOPE_QAS') ||
    process.env.HR_HCC_API_SCOPE_QAS ||
    warnOnMissing('HR_HCC_API_SCOPE_QAS');

export const hrHccApiScopePrd: string =
    runtimeEnv('HR_HCC_API_SCOPE_PRD') ||
    process.env.HR_HCC_API_SCOPE_PRD ||
    warnOnMissing('HR_HCC_API_SCOPE_PRD');

export const hrDeiApiScopeDev: string =
    runtimeEnv('HR_DEI_API_SCOPE_DEV') ||
    process.env.HR_DEI_API_SCOPE_DEV ||
    warnOnMissing('HR_DEI_API_SCOPE_DEV');

export const hrEmployeeListApiScopeDev: string =
    runtimeEnv('HR_EMPLOYEELIST_API_SCOPE_DEV') ||
    process.env.HR_EMPLOYEELIST_API_SCOPE_DEV ||
    warnOnMissing('HR_EMPLOYEELIST_API_SCOPE_DEV');

export const databricksProxyApiBaseUrlDev: string =
    runtimeEnv('DATABRICKS_PROXY_API_BASE_URL_DEV') ||
    process.env.DATABRICKS_PROXY_API_BASE_URL_DEV ||
    warnOnMissing('DATABRICKS_PROXY_API_BASE_URL_DEV');

export const databricksProxyApiScopeDev: string =
    runtimeEnv('DATABRICKS_PROXY_API_SCOPE_DEV') ||
    process.env.DATABRICKS_PROXY_API_SCOPE_DEV ||
    warnOnMissing('DATABRICKS_PROXY_API_SCOPE_DEV');

export const databricksProxyApiBaseUrlPrd: string =
    runtimeEnv('DATABRICKS_PROXY_API_BASE_URL_PRD') ||
    process.env.DATABRICKS_PROXY_API_BASE_URL_PRD ||
    warnOnMissing('DATABRICKS_PROXY_API_BASE_URL_PRD');

export const databricksProxyApiScopePrd: string =
    runtimeEnv('DATABRICKS_PROXY_API_SCOPE_PRD') ||
    process.env.DATABRICKS_PROXY_API_SCOPE_PRD ||
    warnOnMissing('DATABRICKS_PROXY_API_SCOPE_PRD');

export const insightsNowApiScopeDev: string =
    runtimeEnv('INSIGHTS_NOW_API_SCOPE_DEV') ||
    process.env.INSIGHTS_NOW_API_SCOPE_DEV ||
    warnOnMissing('INSIGHTS_NOW_API_SCOPE_DEV');

export const insightsNowApiScopePrd: string =
    runtimeEnv('INSIGHTS_NOW_API_SCOPE_PRD') ||
    process.env.INSIGHTS_NOW_API_SCOPE_PRD ||
    warnOnMissing('INSIGHTS_NOW_API_SCOPE_PRD');

export const cloudWatchRUM: Record<
    'dev' | 'prod',
    { region: string; appId: string; identityPool: string; guestRoleArn: string }
> = {
    dev: {
        region: runtimeEnv('CLOUDWATCH_RUM_REGION') || process.env.CLOUDWATCH_RUM_REGION || '',
        appId: runtimeEnv('CLOUDWATCH_RUM_APP_ID') || process.env.CLOUDWATCH_RUM_APP_ID || '',
        identityPool:
            runtimeEnv('CLOUDWATCH_RUM_IDENTITY_POOL') ||
            process.env.CLOUDWATCH_RUM_IDENTITY_POOL ||
            '',
        guestRoleArn:
            runtimeEnv('CLOUDWATCH_RUM_GUEST_ROLE_ARN') ||
            process.env.CLOUDWATCH_RUM_GUEST_ROLE_ARN ||
            '',
    },
    prod: {
        region:
            runtimeEnv('CLOUDWATCH_RUM_REGION__PROD') ||
            process.env.CLOUDWATCH_RUM_REGION__PROD ||
            '',
        appId:
            runtimeEnv('CLOUDWATCH_RUM_APP_ID__PROD') ||
            process.env.CLOUDWATCH_RUM_APP_ID__PROD ||
            '',
        identityPool:
            runtimeEnv('CLOUDWATCH_RUM_IDENTITY_POOL__PROD') ||
            process.env.CLOUDWATCH_RUM_IDENTITY_POOL__PROD ||
            '',
        guestRoleArn:
            runtimeEnv('CLOUDWATCH_RUM_GUEST_ROLE_ARN__PROD') ||
            process.env.CLOUDWATCH_RUM_GUEST_ROLE_ARN__PROD ||
            '',
    },
};

/**
 * Allows for adjusting environment variables without rebuilding the project.
 * Checking is window exists in case package is loaded in node environment or in tests.
 */
function runtimeEnv(name: string): string | undefined {
    return typeof window !== 'undefined' ? window.__RUNTIME_ENV__?.[name] : undefined;
}

function warnOnMissing(name: string): string {
    if (!isTest) console.warn(`Missing "${name}" env variable, fallbacking to empty string`);

    return '';
}
